import { createContext, useContext, useEffect, useState } from 'react';
import { fetchSite } from '../api/site';
import { course as staticCourse } from '../data/course';
import { WALLETS } from '../lib/payment';

// course: static content + current batch fields. notice: { text, link } or null. payment: [{ method, number }].
const fallbackSite = { course: staticCourse, notice: null, payment: [] };

const SiteContext = createContext(fallbackSite);

const isSite = (data) => data && typeof data.course === 'object' && data.course && Array.isArray(data.payment);

// Only http(s) links and in-page anchors are rendered.
const safeLink = (link) => (typeof link === 'string' && /^(https?:\/\/|#)/.test(link) ? link : null);

// Batch values from the server win, including nulls (null = "hide this"). Unknown wallets are dropped.
function fromApi(data) {
  const { notice } = data;
  return {
    course: { ...staticCourse, ...data.course },
    notice: notice && typeof notice.text === 'string' && notice.text ? { text: notice.text, link: safeLink(notice.link) } : null,
    payment: data.payment.filter((p) => p && WALLETS[p.method] && typeof p.number === 'string' && p.number),
  };
}

// Renders the static content immediately, then swaps in the live batch, notice and payment numbers from
// GET /api/site. If the request fails, the fallback stays so the page never renders blank.
export function SiteProvider({ children }) {
  const [site, setSite] = useState(fallbackSite);

  useEffect(() => {
    const controller = new AbortController();
    fetchSite({ signal: controller.signal })
      .then((data) => {
        if (isSite(data)) setSite(fromApi(data));
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          // eslint-disable-next-line no-console
          console.warn('[site] Using fallback content:', err.message);
        }
      });
    return () => controller.abort();
  }, []);

  return <SiteContext.Provider value={site}>{children}</SiteContext.Provider>;
}

export const useSite = () => useContext(SiteContext);
